/**
 * App header (BUILD_SPEC.md §10).
 *
 * Layout strategy: at viewports ≥ md the three setting toggles
 * (theme/lang/sound) sit inline next to the username and logout. On
 * narrower screens they collapse into a single Settings popover so the
 * header doesn't crowd into a horizontal scroll on phones — the spec
 * doesn't mandate phones, but staff will check the dashboard from a
 * pocket from time to time and a cluttered header signals "broken."
 *
 * The popover is built with native `<details>` for two reasons:
 *   • it's accessible by default (keyboard, screen reader);
 *   • dismissal-on-outside-click is free via the browser.
 */

import { useTranslation } from "react-i18next";
import { LogOut, Settings, Shield, UserCircle2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { ThemeToggle } from "./ThemeToggle";
import { LangToggle } from "./LangToggle";
import { SoundToggle } from "./SoundToggle";
import { HeaderLogo } from "./HeaderLogo";

export function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // The Header only renders inside AppLayout (ADMIN + EMPLOYEE). DISPLAY
  // (TV kiosk) never reaches it (AppLayout redirects that role to the
  // full-screen /wall), so there is no kiosk-chrome special-casing here.

  const onLogout = async () => {
    await logout();
    // state.logout=true signals "explicit sign-out" to LoginPage and
    // RedirectIfAuthed so they default to /dashboard on next login
    // instead of honoring a stale state.from. Without this flag, races
    // between auth-store clear() and our explicit navigate() can let
    // RequireAuth's bounce stick state.from=<current admin path> on the
    // /login entry, which then "resumes" the admin page after re-login.
    navigate("/login", { replace: true, state: { logout: true } });
  };

  return (
    <header className="border-b border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-4">
        {/* Logo and username cluster at the start edge: a link to
            /dashboard so staff can return home from any page by clicking
            the logo or their username. */}
        <Link
          to="/dashboard"
          aria-label={t("common.dashboard")}
          className="flex items-center gap-2 min-w-0 rounded-md px-1 py-1 hover:bg-hairline/40 dark:hover:bg-hairline-dark/40 transition-colors"
        >
          <HeaderLogo />
          {user && (
            <span className="rtl-isolate text-base font-bold text-fg dark:text-fg-dark truncate hidden sm:inline">
              {user.username}
            </span>
          )}
        </Link>

        <div className="flex items-center gap-2">
          {/* Admin entry — only rendered for ADMIN role. Links to /admin,
              which the router redirects to /admin/transactions; the
              AdminLayout sidebar handles in-area navigation from there.
              Suppressed for DISPLAY via the role check (v1.1 B3). */}
          {user?.role === "ADMIN" && (
            <Link
              to="/admin"
              aria-label={t("common.admin")}
              title={t("common.admin")}
              className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40 transition-colors"
            >
              <Shield className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">{t("common.admin")}</span>
            </Link>
          )}

          {/* Inline controls visible on tablet+; they collapse below md
              into the Settings popover to keep the header tight on
              narrow screens. */}
          <div className="hidden md:flex items-center gap-1">
            <ThemeToggle size="sm" />
            <LangToggle size="sm" />
            <SoundToggle size="sm" />
          </div>

          <details className="md:hidden relative">
            <summary
              aria-label={t("common.settings")}
              className="list-none cursor-pointer rounded-md p-2 text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
            >
              <Settings className="h-5 w-5" aria-hidden />
            </summary>
            {/* `end-0` is the RTL-aware version of `right-0` from the
                tailwindcss-rtl plugin — flips automatically with dir. */}
            <div className="absolute end-0 mt-1 w-48 rounded-md border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-2 shadow-md z-10">
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-sm text-muted dark:text-muted-dark">
                  {t("common.theme")}
                </span>
                <ThemeToggle size="sm" />
              </div>
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-sm text-muted dark:text-muted-dark">
                  {t("common.language")}
                </span>
                <LangToggle size="sm" />
              </div>
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-sm text-muted dark:text-muted-dark">
                  {t("common.sound")}
                </span>
                <SoundToggle size="sm" />
              </div>
            </div>
          </details>

          {/* Role chip and logout at the trailing edge. The chip shows the
              account role label only; hovering reveals the username via
              the title attribute. */}
          {user && (
            <div className="flex items-center gap-2 ms-2 ps-2 border-s border-hairline dark:border-hairline-dark">
              <span
                className="hidden sm:inline-flex items-center gap-1.5 text-xs text-muted dark:text-muted-dark"
                title={user.username}
              >
                <UserCircle2 className="h-4 w-4 shrink-0" aria-hidden />
                {user.role && (
                  <span className="rtl-isolate">{t(`users.${user.role}`)}</span>
                )}
              </span>
              <button
                type="button"
                onClick={onLogout}
                aria-label={t("common.logout")}
                title={t("common.logout")}
                className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40 transition-colors"
              >
                <LogOut className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">{t("common.logout")}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
